import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const navLinks = [
  { to: '/', label: 'Home' },
  {
    label: 'About us',
    children: [
      { to: '/about', label: 'About Us' },
      { to: '/mission-vision', label: 'Mission and Vision' },
      { to: '/constitution', label: 'Constitution' },
    ],
  },
  { to: '/executive-members', label: 'Executives' },
  { to: '/gallery', label: 'Event' },
  { to: '/scaleoffees', label: 'Scale of Fees' },
  { to: '/find-surveyor', label: 'Find a Surveyor' },
  { to: '/contact', label: 'Contact' },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setDropdownOpen(false); setMobileAboutOpen(false); }, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => { logout(); navigate('/'); };

  const isAboutActive = navLinks.find(l => l.children)?.children.some(c => location.pathname === c.to);

  const [searchOpen, setSearchOpen] = useState(false);

  return (
    <header className={`sticky top-0 z-50 bg-white transition-shadow duration-300 ${scrolled ? 'shadow-lg' : ''}`}>
      {/* Brand Header — White background, logos left, MEMBER LOGIN / JOIN right */}
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between py-4">
          {/* Left: Logos + APPSN text */}
          <Link to="/" className="flex items-center gap-2.5">
            <img
              src="/FCT_NIS_LOGO.png"
              alt="NIS FCT Branch"
              className="h-12 md:h-20 w-auto object-contain flex-shrink-0"
              style={{ mixBlendMode: 'multiply' }}
            />

            <div className="flex items-center gap-1.5 min-w-0">
              <img
                src="/APPSN.jpg"
                alt="APPSN"
                className="h-10 w-10 md:h-16 md:w-16 rounded-full object-contain bg-white flex-shrink-0"
              />
              <div className="min-w-0">
                <span className="block text-base md:text-4xl font-extrabold text-black leading-none tracking-tight whitespace-nowrap" style={{ fontFamily: "'Inter', sans-serif" }}>
                  APPSN - FCT Branch
                </span>
                <span className="block text-[10px] md:text-xs text-gray-600 leading-tight mt-0.5">
                  Association of Private Practising<br />Surveyors of Nigeria
                </span>
              </div>
            </div>
          </Link>

          {/* Right: MEMBER LOGIN / JOIN */}
          <div className="hidden md:flex items-center gap-5 text-sm">
            {user ? (
              <>
                <Link
                  to={user.role === 'admin' ? '/admin' : '/portal'}
                  className="text-gray-700 hover:text-black font-medium tracking-wide uppercase transition"
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-gray-700 hover:text-black font-medium tracking-wide uppercase transition"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-black font-medium tracking-wide uppercase transition"
                >
                  Member Login
                </Link>
                <Link
                  to="/register"
                  className="text-gray-700 hover:text-black font-medium tracking-wide uppercase transition"
                >
                  Join
                </Link>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden text-gray-700 p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle navigation"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Navigation Bar — thin border top, clean black text */}
      <nav className="border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="hidden md:flex items-center h-12">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.label} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className={`px-5 h-12 text-[15px] font-medium transition-colors flex items-center gap-1 border-b-2 ${
                      isAboutActive
                        ? 'text-black border-[#0B5D1E]'
                        : 'text-gray-700 border-transparent hover:text-black hover:border-gray-300'
                    }`}
                  >
                    {link.label}
                    <svg className={`w-3 h-3 ml-0.5 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {dropdownOpen && (
                    <div className="absolute top-full left-0 mt-0 w-52 bg-white rounded-b-lg shadow-xl border border-gray-100 py-1.5 z-50">
                      {link.children.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          className={`block px-5 py-2.5 text-sm transition ${
                            location.pathname === child.to
                              ? 'text-[#0B5D1E] bg-gray-50 font-semibold'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-black'
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-5 h-12 text-[15px] font-medium flex items-center transition-colors border-b-2 ${
                    location.pathname === link.to
                      ? 'text-black border-[#0B5D1E]'
                      : 'text-gray-700 border-transparent hover:text-black hover:border-gray-300'
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}

            {/* Search button — dark red */}
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="ml-auto bg-[#8B1A1A] hover:bg-[#6F1515] text-white w-11 h-11 flex items-center justify-center rounded transition"
              aria-label="Search"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Search bar (toggle) */}
        {searchOpen && (
          <div className="border-t border-gray-100 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 py-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Search..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#0B5D1E]"
                  autoFocus
                />
                <button
                  onClick={() => setSearchOpen(false)}
                  className="text-gray-400 hover:text-gray-600 p-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Mobile Navigation */}
      <div className={`md:hidden overflow-hidden transition-all duration-300 ${mobileOpen ? 'max-h-[600px] border-t border-gray-200' : 'max-h-0'}`}>
        <div className="px-4 py-3 space-y-0.5 bg-white">
          {navLinks.map((link) =>
            link.children ? (
              <div key={link.label}>
                <button
                  onClick={() => setMobileAboutOpen(!mobileAboutOpen)}
                  className={`w-full flex items-center justify-between px-3 py-3 text-sm font-medium transition ${
                    isAboutActive ? 'text-[#0B5D1E] font-semibold' : 'text-gray-700 hover:text-black'
                  }`}
                >
                  {link.label}
                  <svg className={`w-4 h-4 transition-transform ${mobileAboutOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {mobileAboutOpen && (
                  <div className="ml-4 space-y-0.5 border-l-2 border-gray-200 pl-3">
                    {link.children.map((child) => (
                      <Link
                        key={child.to}
                        to={child.to}
                        className={`block px-3 py-2 text-sm transition ${
                          location.pathname === child.to
                            ? 'text-[#0B5D1E] font-semibold'
                            : 'text-gray-500 hover:text-black'
                        }`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link.to}
                to={link.to}
                className={`block px-3 py-3 text-sm font-medium transition ${
                  location.pathname === link.to
                    ? 'text-[#0B5D1E] font-semibold'
                    : 'text-gray-700 hover:text-black'
                }`}
              >
                {link.label}
              </Link>
            )
          )}
          <hr className="border-gray-200 my-2" />
          {user ? (
            <>
              <Link to={user.role === 'admin' ? '/admin' : '/portal'} className="block px-3 py-3 text-sm font-medium text-gray-700 hover:text-black">
                Dashboard
              </Link>
              <button onClick={handleLogout} className="w-full text-left px-3 py-3 text-sm font-medium text-[#8B1A1A]">
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="block px-3 py-3 text-sm font-medium text-gray-700 hover:text-black">
                Member Login
              </Link>
              <Link to="/register" className="block px-3 py-3 text-sm font-medium text-[#0B5D1E]">
                Join
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
